/**
 * Biometric authentication helper.
 *
 * Capacitor 8 compatible: uses
 *   - @aparajita/capacitor-biometric-auth  → fingerprint / face prompt
 *   - @aparajita/capacitor-secure-storage  → encrypted credential storage
 *     (Android Keystore / iOS Keychain)
 *
 * The previous `capacitor-native-biometric` plugin was built for Capacitor 3/4
 * and its native methods hung indefinitely under Capacitor 8.
 *
 * Optional Face ID / Touch ID / fingerprint gate for quick re-login on the
 * technician app. Entirely opt-in: the app works without it, and every function
 * is a safe no-op on web (returns "unavailable" / false).
 */
import { isNative } from './platform'
// Static imports (not dynamic import()) — a dynamic chunk for these plugins was
// never resolving inside the Capacitor WebView, which made every biometric call
// appear to "time out". Bundling them into the main entry fixes that. Both
// plugins ship safe web implementations, so this is also safe on web.
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth'
import { SecureStorage } from '@aparajita/capacitor-secure-storage'
import { configureNativeSecureStorage, nativeSecureSetString } from './secureStorageConfig'
import { secureGet, secureRemove } from './secureStorage'
import { getRefreshToken, loadStoredTokens, setRefreshToken } from '@/auth/tokenManager'
import {
  markBiometricLoginConfigured,
  clearBiometricLoginConfigured,
} from './biometricEnrollment'

/** Legacy password storage key — migrated away; purged on startup. */
const LEGACY_CREDENTIAL_KEY = 'fsj_biometric_credentials'
/** Phone hint for token-gated biometric quick login (non-secret). */
export const TOKEN_QUICK_LOGIN_PHONE_KEY = 'token_quick_login_phone'
/**
 * Dedicated refresh-token copy for biometric unlock only.
 * Survives Sign Out (session refresh_token is cleared on full logout); wiped
 * only when the user disables biometric login.
 */
export const BIOMETRIC_REFRESH_TOKEN_KEY = 'biometric_refresh_token'

function _getBio() {
  if (!isNative()) return null
  return BiometricAuth || null
}

function _getStorage() {
  if (!isNative()) return null
  return SecureStorage || null
}

/**
 * Raw availability status.
 * @returns {Promise<{isAvailable: boolean, deviceIsSecure?: boolean, code?: string, reason?: string}>}
 */
export async function getBiometricAvailability() {
  const Bio = _getBio()
  if (!Bio) return { isAvailable: false, code: 'plugin-not-loaded' }
  try {
    const result = await Bio.checkBiometry()
    return result || { isAvailable: false }
  } catch (e) {
    console.warn('[Biometric] checkBiometry threw:', e?.message || e)
    return { isAvailable: false, code: 'check-failed' }
  }
}

/**
 * Whether biometric auth is available and enrolled on this device.
 * @returns {Promise<boolean>}
 */
export async function isBiometricAvailable() {
  const result = await getBiometricAvailability()
  return !!result?.isAvailable
}

/**
 * Prompt the user for a biometric check.
 * @param {object} [opts]
 * @param {string} [opts.reason] reason shown in the system prompt
 * @returns {Promise<boolean>} true if verified
 */
export async function verifyIdentity(opts = {}) {
  const Bio = _getBio()
  if (!Bio) return false
  try {
    await Bio.authenticate({
      reason: opts.reason || 'Confirm your identity',
      androidTitle: 'FiveStarJob',
      androidSubtitle: 'Sign in with biometrics',
      // Let the user fall back to device PIN/pattern/password.
      allowDeviceCredential: true,
      androidConfirmationRequired: false,
    })
    return true
  } catch (e) {
    // Rejected / failed / cancelled.
    console.warn('[Biometric] authenticate failed:', e?.code || e?.message || e)
    return false
  }
}

/**
 * Enable biometric quick login gated by the refresh token in secure storage.
 * Passwords are never persisted on device.
 * @param {string} phone normalized E.164 phone
 * @returns {Promise<boolean>}
 */
export async function enableTokenQuickLogin(phone) {
  const r = await enableTokenQuickLoginDetailed(phone)
  return r.ok
}

/**
 * @returns {Promise<{ok: boolean, error?: string}>}
 */
export async function saveBiometricRefreshToken(token) {
  if (!isNative() || !token) return
  await configureNativeSecureStorage()
  await nativeSecureSetString(BIOMETRIC_REFRESH_TOKEN_KEY, token)
}

export async function getBiometricRefreshToken() {
  if (!isNative()) return null
  return secureGet(BIOMETRIC_REFRESH_TOKEN_KEY)
}

export async function clearBiometricRefreshToken() {
  if (!isNative()) return
  await secureRemove(BIOMETRIC_REFRESH_TOKEN_KEY)
}

/**
 * Restore session refresh from the biometric vault when Sign Out cleared it.
 * @returns {Promise<string|null>}
 */
export async function ensureRefreshTokenForBiometricUnlock() {
  await loadStoredTokens()
  let rt = getRefreshToken()
  if (rt) return rt
  rt = await secureGet('refresh_token')
  if (rt) {
    setRefreshToken(rt)
    return rt
  }
  rt = await getBiometricRefreshToken()
  if (rt) {
    setRefreshToken(rt)
    return rt
  }
  return null
}

export async function enableTokenQuickLoginDetailed(phone) {
  if (!isNative()) return { ok: false, error: 'not-native' }
  if (!phone) return { ok: false, error: 'missing-phone' }
  await loadStoredTokens()
  const rt = await ensureRefreshTokenForBiometricUnlock()
  if (!rt) return { ok: false, error: 'missing-refresh-token' }
  try {
    await configureNativeSecureStorage()
    await nativeSecureSetString(TOKEN_QUICK_LOGIN_PHONE_KEY, phone)
    await saveBiometricRefreshToken(rt)
    markBiometricLoginConfigured(phone)
    await purgeLegacyPasswordCredentials()
    return { ok: true }
  } catch (e) {
    const msg = e?.code || e?.message || String(e)
    console.warn('[Biometric] enableTokenQuickLogin failed:', msg)
    return { ok: false, error: msg }
  }
}

/**
 * @returns {Promise<string|null>} phone hint for token quick login, or null
 */
export async function getTokenQuickLoginPhone() {
  if (!isNative()) return null
  try {
    await configureNativeSecureStorage()
    const phone = await secureGet(TOKEN_QUICK_LOGIN_PHONE_KEY)
    if (phone == null || phone === '') return null
    const normalized = String(phone).trim()
    return normalized || null
  } catch {
    return null
  }
}

/**
 * Whether token-gated biometric quick login can be offered.
 * Requires the Keystore phone hint written by enableTokenQuickLoginDetailed
 * (not only the localStorage "configured" flag — that flag alone caused iOS
 * to skip the enroll dialog while logged in with a session refresh token).
 * @returns {Promise<boolean>}
 */
export async function isTokenQuickLoginAvailable() {
  if (!isNative()) return false
  await loadStoredTokens()
  const phone = await getTokenQuickLoginPhone()
  if (!phone) return false
  if (getRefreshToken()) return true
  return !!(await getBiometricRefreshToken())
}

/**
 * Remove legacy password credentials from secure storage (one-time / logout).
 * @returns {Promise<void>}
 */
export async function purgeLegacyPasswordCredentials() {
  const Storage = _getStorage()
  if (!Storage) return
  try { await Storage.remove(LEGACY_CREDENTIAL_KEY) } catch { /* ignore */ }
}

/** @deprecated Password storage removed — use enableTokenQuickLogin(). */
export async function saveCredentials(username) {
  return enableTokenQuickLogin(username)
}

/** @deprecated Password storage removed — use enableTokenQuickLoginDetailed(). */
export async function saveCredentialsDetailed(username) {
  return enableTokenQuickLoginDetailed(username)
}

/** @deprecated Passwords are no longer stored. Returns null always. */
export async function getCredentials() {
  return null
}

/**
 * Delete stored credentials (call on logout / disable).
 * @returns {Promise<void>}
 */
export async function deleteCredentials() {
  if (!isNative()) return
  try { await secureRemove(LEGACY_CREDENTIAL_KEY) } catch { /* ignore */ }
  try { await secureRemove(TOKEN_QUICK_LOGIN_PHONE_KEY) } catch { /* ignore */ }
  try { await clearBiometricRefreshToken() } catch { /* ignore */ }
  clearBiometricLoginConfigured()
  try {
    const { clearLastBiometricAt } = await import('./identityGate')
    await clearLastBiometricAt()
  } catch { /* ignore */ }
  try {
    const { clearPasskeyLocal } = await import('./passkey')
    await clearPasskeyLocal()
  } catch { /* ignore */ }
}
